import { cliente } from '@/lib/cliente';
import type {
  ActualizarPropuestaPagoSchema,
  CrearPropuestaPagoSchema,
  SincronizacionCreditoResultado,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { z } from 'zod';

/** Avisa si quedaron proveedores de la propuesta sin reflejar en Proveedores Crédito. */
function avisarSinMatch(sincronizacionCredito: SincronizacionCreditoResultado | undefined) {
  if (!sincronizacionCredito?.proveedoresSinMatch.length) return;
  toast.warning(
    `Proveedores Crédito: no se pudo ubicar en Flujo Semanal a ${sincronizacionCredito.proveedoresSinMatch.join(', ')}`,
  );
}

/** Queries de Flujo Semanal que dependen de flu.pagos y cambian con este sync. */
function invalidarFlujoSemanal(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['semana'] });
  queryClient.invalidateQueries({ queryKey: ['semanas'] });
  queryClient.invalidateQueries({ queryKey: ['pagos-credito'] });
}

export function usePropuestasPago() {
  return useQuery({
    queryKey: ['propuestas-pago'],
    queryFn: () => cliente.propuestasPago.listar(),
  });
}

export function usePropuestaPago(id: number | null) {
  return useQuery({
    queryKey: ['propuestas-pago', id],
    queryFn: () => cliente.propuestasPago.detalle(id as number),
    enabled: id !== null,
  });
}

export function useCrearPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: z.infer<typeof CrearPropuestaPagoSchema>) =>
      cliente.propuestasPago.crear(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      invalidarFlujoSemanal(queryClient);
      avisarSinMatch(res.data?.sincronizacionCredito);
    },
  });
}

export function useActualizarPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: z.infer<typeof ActualizarPropuestaPagoSchema>;
    }) => cliente.propuestasPago.actualizar(id, data),
    onSuccess: (res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      queryClient.invalidateQueries({ queryKey: ['propuestas-pago', variables.id] });
      invalidarFlujoSemanal(queryClient);
      avisarSinMatch(res.data?.sincronizacionCredito);
    },
  });
}

/** Anotar (o quitar) a qué factura se aplicó realmente el pago de una línea de la propuesta. */
export function useAplicacionLineaPropuesta(idPropuesta: number) {
  const queryClient = useQueryClient();
  const refrescar = () => {
    queryClient.invalidateQueries({ queryKey: ['propuestas-pago', idPropuesta] });
    queryClient.invalidateQueries({ queryKey: ['propuestas-pago', idPropuesta, 'reporte'] });
  };
  const anotar = useMutation({
    mutationFn: (v: {
      entryNo: number;
      data: { aplicadaADocumento?: string | null; nota?: string | null };
    }) => cliente.propuestasPago.anotarAplicacion(idPropuesta, v.entryNo, v.data),
    onSuccess: refrescar,
  });
  const quitar = useMutation({
    mutationFn: (entryNo: number) => cliente.propuestasPago.quitarAplicacion(idPropuesta, entryNo),
    onSuccess: refrescar,
  });
  return { anotar, quitar };
}

export function useRefrescarPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number }) => cliente.propuestasPago.refrescarDesdeBC(id),
    // Se devuelve la promesa para que el refetch termine antes de los callbacks del componente.
    onSuccess: async (res, variables) => {
      avisarSinMatch(res.data?.sincronizacionCredito);
      invalidarFlujoSemanal(queryClient);
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago', variables.id] });
    },
  });
}

/** Validar (congelar) la propuesta: queda bloqueada y habilita el reporte por proveedor. */
export function useValidarPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cliente.propuestasPago.validar(id),
    onSuccess: async (_res, id) => {
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago', id] });
      await queryClient.invalidateQueries({ queryKey: ['propuesta-reporte', id] });
    },
  });
}

export function useQuitarValidacionPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cliente.propuestasPago.quitarValidacion(id),
    onSuccess: async (_res, id) => {
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      await queryClient.invalidateQueries({ queryKey: ['propuestas-pago', id] });
      queryClient.removeQueries({ queryKey: ['propuesta-reporte', id] });
    },
  });
}

/** Datos congelados del reporte por proveedor (solo existen si la propuesta está validada). */
export function useReportePropuesta(id: number | null, habilitado: boolean) {
  return useQuery({
    queryKey: ['propuesta-reporte', id],
    queryFn: () => cliente.propuestasPago.reporte(id as number),
    enabled: id !== null && habilitado,
    staleTime: 0,
  });
}

export function useEliminarPropuestaPago() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => cliente.propuestasPago.eliminar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propuestas-pago'] });
      invalidarFlujoSemanal(queryClient);
    },
  });
}

export function useArchivoBancoPropuesta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...params
    }: {
      id: number;
      cuentaBancoOrigen: string;
      planCode: string;
      envioNo: number;
      fecha: string;
    }) => cliente.propuestasPago.archivoBanco(id, params),
    onSuccess: (_resultado, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['propuestas-pago', variables.id, 'archivos-banco'],
      });
      queryClient.invalidateQueries({ queryKey: ['archivo-banco', 'ultimos-por-cuenta'] });
    },
  });
}

/** Genera el archivo .SIN del Banco Nacional (no se guarda: se arma al momento). */
export function useArchivoBancoBNPropuesta() {
  return useMutation({
    mutationFn: ({
      id,
      ...params
    }: {
      id: number;
      cuentaBancoOrigen: string;
      transferenciaNo: number;
      fecha: string;
    }) => cliente.propuestasPago.archivoBancoBN(id, params),
  });
}

/** Últimos archivos de banco ya generados por cuenta bancaria (para no tener que regenerarlos). */
export function useArchivosBancoGuardados(id: number | null) {
  return useQuery({
    queryKey: ['propuestas-pago', id, 'archivos-banco'],
    queryFn: () => cliente.propuestasPago.archivosBancoGuardados(id as number),
    enabled: id !== null,
  });
}

/** Último plan (BOK) y envío usados por cada cuenta bancaria, en todas las propuestas. */
export function useUltimosEnviosPorCuenta() {
  return useQuery({
    queryKey: ['archivo-banco', 'ultimos-por-cuenta'],
    queryFn: () => cliente.propuestasPago.ultimosEnviosPorCuenta(),
  });
}

/**
 * Proveedores con la cuenta incompleta (sin ficha, sin IBAN o IBAN dudoso) para una cuenta de
 * origen de la propuesta — se revisa antes de generar el archivo de banco.
 */
export function useValidacionArchivoBanco(id: number, cuentaBancoOrigen: string, enabled: boolean) {
  return useQuery({
    queryKey: ['propuestas-pago', id, 'archivo-banco-validacion', cuentaBancoOrigen],
    queryFn: () => cliente.propuestasPago.validacionArchivoBanco(id, cuentaBancoOrigen),
    enabled: enabled && !!cuentaBancoOrigen,
    // Se corrige en otra pestaña (Proveedores) y se vuelve: que refresque al volver.
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Diarios (secciones) de la plantilla de pagos de BC, para elegir dónde dejar los pagos. */
export function useDiariosPagosBC(enabled: boolean) {
  return useQuery({
    queryKey: ['bc-diarios-pagos'],
    queryFn: () => cliente.bcDiariosPagos.listar(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Qué se enviaría a BC para una cuenta de banco de la propuesta (no escribe nada). */
export function useVistaPreviaEnvioBC(
  id: number,
  cuentaBancoOrigen: string,
  diario: { template: string; batch: string } | undefined,
  enabled: boolean,
  postingDate?: string,
) {
  return useQuery({
    queryKey: [
      'propuestas-pago',
      id,
      'envio-bc',
      cuentaBancoOrigen,
      diario?.template ?? null,
      diario?.batch ?? null,
      postingDate ?? null,
    ],
    queryFn: () =>
      cliente.propuestasPago.envioBCVistaPrevia(id, cuentaBancoOrigen, diario, postingDate),
    enabled: enabled && !!cuentaBancoOrigen,
    staleTime: 0,
  });
}

export function useEnviarABC() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: number;
      cuentaBancoOrigen: string;
      journalTemplate: string;
      journalBatch: string;
      confirmarDiarioConLineas?: boolean;
      omitirProveedoresConCambios?: boolean;
      postingDate?: string;
    }) => cliente.propuestasPago.enviarABC(id, body),
    onSuccess: (_r, variables) => {
      queryClient.invalidateQueries({ queryKey: ['propuestas-pago', variables.id, 'envio-bc'] });
    },
  });
}
